import * as z from "zod";
import { decimal, pagination, validationIssue, version } from "../index";
import { creditOutcomes } from "./enums";
import { orderAmount, reason } from "./fields";

// Credit decisions (M10, ADR-112, ADR-179; 50-api/sales.md §2.4): what Credit Control recorded by hand about an
// order, the override of a block, and what Business Central said (I-06) before its decisions were ignored. The
// approval of Hilldun on the orders of an insured customer, one by one or from a file (ADR-185).

/**
 * A credit check Credit Control records by hand (B-03) about one order. A block says why. What holds for every order
 * of a customer is in the directory: fulfillment forced, hold, blacklist, insurer (ADR-179).
 */
export const recordCreditDecisionInput = z
  .object({
    salesOrderId: z.uuid(),
    outcome: z.enum(["approved", "blocked"]),
    reason: reason.optional(),
    /** Rating of the customer at the source of the check. */
    rating: z.string().trim().min(1).max(40).optional(),
  })
  .refine((d) => d.outcome !== "blocked" || d.reason !== undefined, {
    path: ["reason"],
    message: "a block says why",
  });
export type RecordCreditDecisionInput = z.input<typeof recordCreditDecisionInput>;

/** Latest decided first: every decision about a customer, or the ones about one order. */
export const listCreditDecisionsInput = z.object({
  customer: z.uuid().optional(),
  salesOrder: z.uuid().optional(),
  /** The decisions an order reads (ADR-124): its own, and the ones about its customer, not those of its other orders. */
  forSalesOrder: z.uuid().optional(),
  ...pagination({ defaultSize: 25, maxSize: 100 }),
});
export type ListCreditDecisionsInput = z.input<typeof listCreditDecisionsInput>;

/**
 * B-18: Credit Control lets an order blocked for credit go on against the outcome of the check, with the reason.
 * `version` is the one the client read (409 otherwise).
 */
export const overrideCreditBlockInput = z.object({ salesOrderId: z.uuid(), version, reason });
export type OverrideCreditBlockInput = z.input<typeof overrideCreditBlockInput>;
export const overrideCreditBlockBody = overrideCreditBlockInput.omit({ salesOrderId: true });

/** A credit decision, with who it is about and where it came from. */
export const creditDecisionDto = z
  .object({
    id: z.uuid(),
    customerOrgId: z.uuid(),
    customer: z.string(),
    /** The order the decision is about; null, it is about the customer. */
    salesOrderId: z.uuid().nullable(),
    soNumber: z.string().nullable(),
    outcome: z.enum(creditOutcomes),
    /** Who decided: `manual` for Credit Control in Nexxt, otherwise what Business Central names (`cribis`, …). */
    source: z.string(),
    /** The decision arrived from Business Central (I-06). */
    fromBusinessCentral: z.boolean(),
    rating: z.string().nullable().optional(),
    creditLimit: decimal.nullable().optional(),
    /** What the customer owes and has on order, as Business Central counts it. */
    exposure: decimal.nullable().optional(),
    currency: z.string().nullable().optional(),
    reason: z.string().nullable(),
    /** Name of the person who recorded it in Nexxt; null for a decision of Business Central. */
    decidedBy: z.string().nullable(),
    decidedAt: z.iso.datetime(),
  })
  .meta({ id: "CreditDecision" });

const hilldunApprovalFields = z.object({
  version,
  /** False: Hilldun declined the order. */
  approved: z.boolean(),
  /**
   * The amount Hilldun approved: an approval may have it, zero kept as NetSuite keeps it (the flag is what counts,
   * ADR-296); a decline has none.
   */
  amount: orderAmount.optional(),
});

/** A decline has no amount. */
const amountWithApproval = (d: { approved: boolean; amount?: string | undefined }) =>
  d.approved || d.amount === undefined;
const amountIssue = { path: ["amount"], message: "a decline has no amount" };

/**
 * ADR-185, ADR-296: Credit Control records what Hilldun said of an order of an insured customer, with the amount it
 * approved when there is one.
 * The default of the customer is applied again to the order, unless it has a decision of its own. `version` is the
 * one the client read (409 otherwise).
 */
export const recordHilldunApprovalInput = hilldunApprovalFields
  .extend({ salesOrderId: z.uuid() })
  .refine(amountWithApproval, amountIssue);
export type RecordHilldunApprovalInput = z.input<typeof recordHilldunApprovalInput>;
export const recordHilldunApprovalBody = hilldunApprovalFields.refine(amountWithApproval, amountIssue);

/** The most rows a file of approvals applies in one request, in one transaction (ADR-185). */
export const HILLDUN_FILE_MAX_ROWS = 5000;

/**
 * The columns of a file of approvals of Hilldun (ADR-185): the header Nexxt writes in its template, and the ones the
 * import "creditreq update" of NetSuite read, so the file of today is taken as it is. The app finds each column by
 * its header, without case and spaces around.
 */
export const hilldunFileColumns = [
  { field: "soNumber", header: "SO Number", aliases: ["ponumber", "sales order", "so"] },
  { field: "approval", header: "Hilldun Credit Approval", aliases: ["hilldun approval", "approval"] },
  { field: "amount", header: "Approved Amount", aliases: ["amount forcsv", "amount"] },
] as const;

/** A cell of the file as the app read it: the domain judges what it says. */
const fileCell = z.string().trim().max(60);

/** A row of the file, by its number as the file shows it (the header is row 1). */
export const hilldunFileRow = z.object({
  row: z.number().int().min(1),
  soNumber: fileCell,
  approval: fileCell,
  amount: fileCell,
});
export type HilldunFileRow = z.input<typeof hilldunFileRow>;

/** ADR-185: a file of approvals of Hilldun, as the app read it, applied row by row in one transaction. */
export const importHilldunApprovalsInput = z.object({
  rows: z.array(hilldunFileRow).min(1).max(HILLDUN_FILE_MAX_ROWS),
});
export type ImportHilldunApprovalsInput = z.input<typeof importHilldunApprovalsInput>;

/** What a row of the file did: recorded on its order, the same as already recorded, or refused with its issues. */
export const hilldunRowOutcomes = ["recorded", "unchanged", "rejected"] as const;

/** A row of the file and what it did. */
export const hilldunRowResultDto = z.object({
  row: z.number().int(),
  soNumber: z.string(),
  outcome: z.enum(hilldunRowOutcomes),
  /** The order the row named; null when there is none the Actor sees. */
  salesOrderId: z.uuid().nullable(),
  /** The credit of the order after the file; null for a row refused. */
  creditOutcome: z.enum(creditOutcomes).nullable(),
  /** The order has a decision of its own, which wins over the default: the approval is kept, the credit stays. */
  keptByDecision: z.boolean(),
  /** Why a row was refused, by column (`soNumber`, `approval`, `amount`, `customer`, `status`). */
  issues: z.array(validationIssue),
  /** What a row applied without (ADR-296): an approval with no amount, or zero (`amount` / `no_approved_amount`). */
  warnings: z.array(validationIssue),
});
export type HilldunRowResult = z.output<typeof hilldunRowResultDto>;

/** The outcome of a file, row by row in the order of the file, with the totals. */
export const hilldunImportDto = z
  .object({
    recorded: z.number().int(),
    unchanged: z.number().int(),
    rejected: z.number().int(),
    rows: z.array(hilldunRowResultDto),
  })
  .meta({ id: "HilldunImport" });
export type HilldunImport = z.output<typeof hilldunImportDto>;

/** What Hilldun said of an order, and who recorded it. */
export const hilldunApprovalDto = z.object({
  approved: z.boolean(),
  /** In the currency of the order; null for a decline, and for an approval without one (ADR-296). */
  amount: decimal.nullable(),
  /** Name of who recorded it; null for one migrated from NetSuite. */
  recordedBy: z.string().nullable(),
  recordedAt: z.iso.datetime().nullable(),
});
